"use client";

import { FormEvent, useState } from "react";
import Link from "next/link";
import { formatINR } from "@/lib/stamp-duty/calculate";
import type { DutyBreakdown, StampDutyRule } from "@/lib/stamp-duty/types";
import { StampDutyDisclaimer } from "./StampDutyDisclaimer";

interface StampDutyResultPanelProps {
  rule: StampDutyRule;
  value: number;
  breakdown: DutyBreakdown;
  source: string;
  selectedClauseAddOns: string[];
}

export function StampDutyResultPanel({ rule, value, breakdown, source, selectedClauseAddOns }: StampDutyResultPanelProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch("/api/stamp-duty-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          state_code: rule.stateCode,
          instrument_slug: rule.instrumentSlug,
          value,
          selected_clause_add_ons: selectedClauseAddOns,
          source,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || "Could not save this calculation.");
      }

      setStatus("success");
      setMessage("Calculation saved.");
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Could not save this calculation.");
    }
  }

  return (
    <section aria-label="Stamp duty result" className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm md:p-6">
      <div className="grid gap-4 md:grid-cols-[1fr_0.8fr]">
        <div>
          <p className="text-sm font-medium text-gray-500">Total stamp duty</p>
          <p className="mt-1 font-serif text-4xl text-gray-950">{formatINR(breakdown.totalStampDuty)}</p>
        </div>
        <form onSubmit={handleSubmit} className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <label htmlFor="stamp-duty-email" className="block text-sm font-medium text-gray-800">
            Email me this calculation
          </label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              id="stamp-duty-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              className="min-h-10 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="min-h-10 rounded-md bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-60"
            >
              {status === "submitting" ? "Saving" : "Send"}
            </button>
          </div>
          {message && (
            <p className={`mt-2 text-xs ${status === "success" ? "text-green-700" : "text-red-700"}`}>
              {message}
            </p>
          )}
        </form>
      </div>

      <dl className="mt-5 grid gap-3 border-y border-gray-100 py-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase text-gray-500">Registration fee</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">
            {rule.registrationFee ? formatINR(breakdown.registrationFee) : "Not included"}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase text-gray-500">Total payable</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">{formatINR(breakdown.totalPayable)}</dd>
        </div>
      </dl>

      <div className="mt-5">
        <h2 className="text-lg font-semibold text-gray-900">Breakdown / working</h2>
        <ol className="mt-3 space-y-2 text-sm leading-6 text-gray-700">
          {breakdown.steps.map((step, index) => (
            <li key={`${step}-${index}`} className="flex gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-700">
        <h2 className="font-semibold text-gray-900">Statutory reference</h2>
        <p className="mt-2">{rule.citation}</p>
        <p>{rule.actName}</p>
        {breakdown.clauseAddOns.length > 0 && (
          <div className="mt-3">
            <p className="font-medium text-gray-900">Additional clause references</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {breakdown.clauseAddOns.map((addOn) => (
                <li key={addOn.id}>
                  {addOn.label}: {addOn.citation}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mt-2">
          <span className="font-medium text-gray-900">As of: </span>
          {rule.effectiveDate}
        </p>
        {rule.sourceUrl ? (
          <a href={rule.sourceUrl} className="font-medium text-primary hover:underline" target="_blank" rel="noreferrer">
            Official source
          </a>
        ) : (
          <p className="text-gray-500">Source link not configured.</p>
        )}
      </div>

      <div className="mt-5">
        <StampDutyDisclaimer />
      </div>

      <div className="mt-5 rounded-lg border border-primary-100 bg-primary-50 p-4">
        <h2 className="text-base font-semibold text-gray-950">Matter already in dispute?</h2>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          Track any Indian court case in real time with India Case Status.
        </p>
        <Link
          href="/track-case"
          className="mt-3 inline-flex min-h-10 items-center rounded-md bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-700"
        >
          Track a court case
        </Link>
      </div>
    </section>
  );
}
